import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import Api from 'src/api';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import hasPermission from 'src/redux/hasPermission';

import {IKisiRolKayit, IRol} from 'src/constants/types/logbook';

import {useDispatch} from 'react-redux';
import {showNotification} from 'src/redux/reducers/notificationReducer';
import {showConfirm} from 'src/redux/reducers/confirmReducer';

interface KisiRolPaneliProps {
    kisiId: number;
    onUpdate?: () => void;
    isProfil?: boolean;
}

const KisiRolPaneli = ({kisiId, onUpdate, isProfil = false}: KisiRolPaneliProps) => {
    const dispatch = useDispatch();
    const [rolKayitlari, setRolKayitlari] = useState<IKisiRolKayit[]>([]);
    const [roles, setRoles] = useState<IRol[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRol, setSelectedRol] = useState<any>({
        id: null,
        rol: '',
        baslangic_tarih: '',
        bitis_tarih: '31-12-2099'
    });

    const canEdit = !isProfil && hasPermission("galleon_logbook.kisiDuzenle");

    const fetchRolKayitlari = async () => {
        try {
            const data = await Api.getPromise('/logbook/yonetim/kisiRol/', {kisiId});
            setRolKayitlari(data);
        } catch (error) {
            console.error("Rol kayıtları çekilemedi", error);
        }
    };

    const fetchRoles = async () => {
        try {
            const data = await Api.getPromise('/logbook/yonetim/rol/');
            setRoles(data);
        } catch (error) {
            console.error("Roller çekilemedi", error);
        }
    };

    useEffect(() => {
        if (kisiId) {
            fetchRolKayitlari();
            fetchRoles();
        }
    }, [kisiId]);

    const handleSave = async () => {
        try {
            const payload = {
                ...selectedRol,
                kisi: kisiId
            };

            if (selectedRol.id) {
                await Api.putPromise('/logbook/yonetim/kisiRol/', payload);
            } else {
                await Api.postPromise('/logbook/yonetim/kisiRol/', payload);
            }

            setOpenModal(false);
            fetchRolKayitlari();
            if (onUpdate) onUpdate();
            dispatch(showNotification({message: "Rol kaydı başarılı.", severity: "success"}));
        } catch (error: any) {
            dispatch(showNotification({message: error.error || "İşlem başarısız.", severity: "error"}));
        }
    };

    const handleDelete = async (id: number) => {
        dispatch(showConfirm({
            message: "Bu rol atamasını silmek istediğinize emin misiniz?",
            onConfirm: async () => {
                try {
                    await Api.deletePromise('/logbook/yonetim/kisiRol/', {id});
                    fetchRolKayitlari();
                    if (onUpdate) onUpdate();
                    dispatch(showNotification({message: "Rol kaydı silindi.", severity: "success"}));
                } catch (error: any) {
                    dispatch(showNotification({message: error.error || "Silme hatası", severity: "error"}));
                }
            }
        }));
    };

    return (
        <Box sx={{mt: 4}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                <Typography variant="h6">Rol Atamaları ve Geçmişi</Typography>
                {canEdit && (
                    <Button
                        color="primary"
                        sx={{marginX: "1rem", minWidth: "10rem"}}
                        startIcon={<AddIcon/>}
                        onClick={() => {
                            setSelectedRol({id: null, rol: '', baslangic_tarih: '', bitis_tarih: '31-12-2099'});
                            setOpenModal(true);
                        }}
                    >
                        Yeni Rol Ata
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Rol</TableCell>
                            <TableCell align="center">Başlangıç</TableCell>
                            <TableCell align="center">Bitiş</TableCell>
                            <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rolKayitlari.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    {roles.find((r: any) => r.id === row.rol)?.rolAdi ?? row.rol}
                                </TableCell>
                                <TableCell align="center">{row.baslangic_tarih}</TableCell>
                                <TableCell align="center">{row.bitis_tarih}</TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <Box>
                                            <IconButton size="small" onClick={() => {
                                                setSelectedRol({
                                                    id: row.id,
                                                    rol: row.rol,
                                                    baslangic_tarih: row.baslangic_tarih,
                                                    bitis_tarih: row.bitis_tarih
                                                });
                                                setOpenModal(true);
                                            }}>
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                                                <DeleteIcon fontSize="small"/>
                                            </IconButton>
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="xs">
                <DialogTitle>{selectedRol.id ? 'Rol Atamasını Düzenle' : 'Yeni Rol Ata'}</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Rol Seçin"
                        value={selectedRol.rol}
                        onChange={(e) => setSelectedRol({...selectedRol, rol: e.target.value})}
                        sx={{mt: 2}}
                    >
                        {roles.map((rol: any) => (
                            <MenuItem key={rol.id} value={rol.id}>{rol.rolAdi}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        label="Başlangıç (GG-AA-YYYY)"
                        placeholder="01-01-2024"
                        value={selectedRol.baslangic_tarih}
                        onChange={(e) => setSelectedRol({...selectedRol, baslangic_tarih: e.target.value})}
                        sx={{mt: 2}}
                    />
                    <TextField
                        fullWidth
                        label="Bitiş (GG-AA-YYYY)"
                        placeholder="31-12-2099"
                        value={selectedRol.bitis_tarih}
                        onChange={(e) => setSelectedRol({...selectedRol, bitis_tarih: e.target.value})}
                        sx={{mt: 2}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>İptal</Button>
                    <Button color="primary" sx={{marginX: "1rem", minWidth: "10rem"}}
                            onClick={handleSave}>Kaydet</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KisiRolPaneli;
